import { enumObject } from './enum-helpers';

/*
 * Domain enumerations. Section references (§) point to SPEC.md.
 * Each enum is exported as a tuple (`*_VALUES`), a union type and a lookup object.
 * Values that are persisted as PostgreSQL enums must stay in sync with
 * apps/api/prisma/schema (a unit test in the API enforces this).
 */

// ── Identity & access (§7, §40.1, §49) ────────────────────────────────────────
export const USER_STATUS_VALUES = ['ACTIVE', 'INACTIVE', 'SUSPENDED'] as const;
export type UserStatus = (typeof USER_STATUS_VALUES)[number];
export const UserStatus = enumObject(USER_STATUS_VALUES);

/**
 * PERSON: someone who signs in. SERVICE: the account an integration client acts as (Phase 3A);
 * it never signs in with a password and holds no user roles.
 */
export const USER_KIND_VALUES = ['PERSON', 'SERVICE'] as const;
export type UserKind = (typeof USER_KIND_VALUES)[number];
export const UserKind = enumObject(USER_KIND_VALUES);

/** Organizational scope attached to a role assignment (§7, "Organization-scoped permissions"). */
export const SCOPE_TYPE_VALUES = ['INSTITUTION', 'COLLEGE', 'DEPARTMENT'] as const;
export type ScopeType = (typeof SCOPE_TYPE_VALUES)[number];
export const ScopeType = enumObject(SCOPE_TYPE_VALUES);

// ── Faculty classification (§8, §10) ───────────────────────────────────────────
/** Seeded employment type codes. Employment types are configurable rows (§7 HR). */
export const EMPLOYMENT_TYPE_CODES = ['FULL_TIME', 'PART_TIME', 'ADJUNCT', 'VISITING', 'OTHER'] as const;
export type EmploymentTypeCode = (typeof EMPLOYMENT_TYPE_CODES)[number];
export const EmploymentTypeCode = enumObject(EMPLOYMENT_TYPE_CODES);

/**
 * How attendance is tracked for an employment type (§8):
 * - DAILY_AND_SESSION: full-time — daily institutional attendance plus teaching/duty sessions.
 * - SESSION_BASED: part-time style — attendance per scheduled class or approved duty.
 */
export const ATTENDANCE_MODEL_VALUES = ['DAILY_AND_SESSION', 'SESSION_BASED'] as const;
export type AttendanceModel = (typeof ATTENDANCE_MODEL_VALUES)[number];
export const AttendanceModel = enumObject(ATTENDANCE_MODEL_VALUES);

export const EMPLOYMENT_STATUS_VALUES = ['ACTIVE', 'INACTIVE', 'ON_LEAVE', 'SEPARATED'] as const;
export type EmploymentStatus = (typeof EMPLOYMENT_STATUS_VALUES)[number];
export const EmploymentStatus = enumObject(EMPLOYMENT_STATUS_VALUES);

/** Full-time daily attendance model (§21): single shift, split shift, or flexible hours. */
export const SHIFT_MODEL_VALUES = ['SINGLE', 'SPLIT', 'FLEXIBLE'] as const;
export type ShiftModel = (typeof SHIFT_MODEL_VALUES)[number];
export const ShiftModel = enumObject(SHIFT_MODEL_VALUES);

// ── Organization & academic master data (§9, §14.1) ────────────────────────────
export const LOCATION_TYPE_VALUES = [
  'CAMPUS',
  'BUILDING',
  'ROOM',
  'LABORATORY',
  'CLINICAL_AFFILIATE',
  'HOSPITAL',
  'COMMUNITY_SITE',
  'APPROVED_REMOTE',
  'TEMPORARY_EVENT',
  'ONLINE',
] as const;
export type LocationType = (typeof LOCATION_TYPE_VALUES)[number];
export const LocationType = enumObject(LOCATION_TYPE_VALUES);

export const ACADEMIC_PERIOD_STATUS_VALUES = ['PLANNED', 'ACTIVE', 'CLOSED', 'ARCHIVED'] as const;
export type AcademicPeriodStatus = (typeof ACADEMIC_PERIOD_STATUS_VALUES)[number];
export const AcademicPeriodStatus = enumObject(ACADEMIC_PERIOD_STATUS_VALUES);

export const TERM_TYPE_VALUES = ['SEMESTER', 'TRIMESTER', 'QUARTER', 'SUMMER', 'MIDYEAR', 'OTHER'] as const;
export type TermType = (typeof TERM_TYPE_VALUES)[number];
export const TermType = enumObject(TERM_TYPE_VALUES);

// ── Scheduling (§11) ───────────────────────────────────────────────────────────
export const SCHEDULED_MODALITY_VALUES = [
  'FACE_TO_FACE',
  'ONLINE_SYNCHRONOUS',
  'HYBRID',
  'REMOTE_APPROVED',
  'FIELD_CLINICAL',
] as const;
export type ScheduledModality = (typeof SCHEDULED_MODALITY_VALUES)[number];
export const ScheduledModality = enumObject(SCHEDULED_MODALITY_VALUES);

/**
 * Actual delivery mode chosen at clock-in (§15.3, AC-003). For hybrid classes the
 * faculty member picks ONSITE, ONLINE or HYBRID_COMBINATION.
 */
export const ACTUAL_MODALITY_VALUES = [
  'ONSITE',
  'ONLINE',
  'HYBRID_COMBINATION',
  'REMOTE_APPROVED',
  'FIELD_CLINICAL',
] as const;
export type ActualModality = (typeof ACTUAL_MODALITY_VALUES)[number];
export const ActualModality = enumObject(ACTUAL_MODALITY_VALUES);

export const LOAD_TYPE_VALUES = [
  'REGULAR',
  'OVERLOAD',
  'EXTRA_LOAD',
  'SUBSTITUTE',
  'MAKEUP',
  'CONSULTATION',
  'ADMINISTRATIVE',
  'CLINICAL',
  'LABORATORY',
  'OTHER',
] as const;
export type LoadType = (typeof LOAD_TYPE_VALUES)[number];
export const LoadType = enumObject(LOAD_TYPE_VALUES);

export const SCHEDULE_STATUS_VALUES = ['DRAFT', 'ACTIVE', 'INACTIVE', 'CANCELLED'] as const;
export type ScheduleStatus = (typeof SCHEDULE_STATUS_VALUES)[number];
export const ScheduleStatus = enumObject(SCHEDULE_STATUS_VALUES);

export const FACULTY_LOAD_STATUS_VALUES = ['DRAFT', 'ACTIVE', 'INACTIVE', 'CANCELLED'] as const;
export type FacultyLoadStatus = (typeof FACULTY_LOAD_STATUS_VALUES)[number];
export const FacultyLoadStatus = enumObject(FACULTY_LOAD_STATUS_VALUES);

export const IMPORT_BATCH_STATUS_VALUES = [
  'UPLOADED',
  'VALIDATED',
  'COMMITTED',
  'FAILED',
  'CANCELLED',
] as const;
export type ImportBatchStatus = (typeof IMPORT_BATCH_STATUS_VALUES)[number];
export const ImportBatchStatus = enumObject(IMPORT_BATCH_STATUS_VALUES);

export const IMPORT_ROW_STATUS_VALUES = ['VALID', 'WARNING', 'ERROR', 'SKIPPED', 'COMMITTED'] as const;
export type ImportRowStatus = (typeof IMPORT_ROW_STATUS_VALUES)[number];
export const ImportRowStatus = enumObject(IMPORT_ROW_STATUS_VALUES);

// ── Attendance capture (§12, §13, §14, §15) ────────────────────────────────────
/**
 * SPEC §12.1. Phase 3G implemented `RFID_FUTURE` as `RFID` (an ID card tapped on a kiosk's card
 * reader) and added `NFC_TAG` (a phone tapped on an NFC room tag) — ADR-062. Phase 3H implemented
 * `BIOMETRIC_FUTURE` as `BIOMETRIC`: a punch of a biometric time clock (ADR-063).
 */
export const ATTENDANCE_METHOD_VALUES = [
  'MOBILE_GPS',
  'WEB_BROWSER',
  'CAMPUS_KIOSK',
  'QR_CODE',
  'MANUAL_APPROVED',
  'API_INTEGRATION',
  'RFID',
  'BIOMETRIC',
  'NFC_TAG',
] as const;
export type AttendanceMethod = (typeof ATTENDANCE_METHOD_VALUES)[number];
export const AttendanceMethod = enumObject(ATTENDANCE_METHOD_VALUES);

export const ATTENDANCE_EVENT_TYPE_VALUES = [
  'CLOCK_IN',
  'CLOCK_OUT',
  'MANUAL_EVENT',
  'KIOSK_SCAN',
  'QR_SCAN',
  'API_EVENT',
] as const;
export type AttendanceEventType = (typeof ATTENDANCE_EVENT_TYPE_VALUES)[number];
export const AttendanceEventType = enumObject(ATTENDANCE_EVENT_TYPE_VALUES);

export const CLOCK_IN_STATUS_VALUES = [
  'EARLY',
  'ON_TIME',
  'LATE',
  'VERY_LATE',
  'MISSING',
  'OUTSIDE_ALLOWED_WINDOW',
] as const;
export type ClockInStatus = (typeof CLOCK_IN_STATUS_VALUES)[number];
export const ClockInStatus = enumObject(CLOCK_IN_STATUS_VALUES);

export const CLOCK_OUT_STATUS_VALUES = [
  'COMPLETE',
  'EARLY_CLOCK_OUT',
  'UNDERTIME',
  'LATE_CLOCK_OUT',
  'MISSING_CLOCK_OUT',
] as const;
export type ClockOutStatus = (typeof CLOCK_OUT_STATUS_VALUES)[number];
export const ClockOutStatus = enumObject(CLOCK_OUT_STATUS_VALUES);

/**
 * §14.3 plus NOT_REQUIRED for sessions where location validation does not apply (online), and
 * VERIFIED_BY_QR: GPS could not confirm the location, a scan of the room's display did (Phase 2E).
 */
export const LOCATION_STATUS_VALUES = [
  'WITHIN_GEOFENCE',
  'OUTSIDE_GEOFENCE',
  'GPS_UNAVAILABLE',
  'GPS_PERMISSION_DENIED',
  'LOW_ACCURACY',
  'APPROVED_REMOTE_LOCATION',
  'LOCATION_REVIEW_REQUIRED',
  'NOT_REQUIRED',
  'VERIFIED_BY_QR',
  /** Tapped an ID card on the card reader of a kiosk at the session's place (Phase 3G). */
  'VERIFIED_BY_CARD_READER',
  /** Tapped the phone on the NFC tag of the session's room (Phase 3G). */
  'VERIFIED_BY_ROOM_TAG',
  /** Punched on a biometric time clock at the session's place (Phase 3H). */
  'VERIFIED_BY_TIME_CLOCK',
] as const;
export type LocationStatus = (typeof LOCATION_STATUS_VALUES)[number];
export const LocationStatus = enumObject(LOCATION_STATUS_VALUES);

export const ONLINE_PLATFORM_VALUES = ['ZOOM', 'GOOGLE_MEET', 'MICROSOFT_TEAMS', 'LMS', 'OTHER'] as const;
export type OnlinePlatform = (typeof ONLINE_PLATFORM_VALUES)[number];
export const OnlinePlatform = enumObject(ONLINE_PLATFORM_VALUES);

/** What an attendance session represents. DAILY = full-time institutional attendance (§8.1, §21). */
export const SESSION_TYPE_VALUES = ['CLASS', 'DUTY', 'DAILY'] as const;
export type SessionType = (typeof SESSION_TYPE_VALUES)[number];
export const SessionType = enumObject(SESSION_TYPE_VALUES);

export const DAILY_SEGMENT_VALUES = ['FULL_DAY', 'AM', 'PM'] as const;
export type DailySegment = (typeof DAILY_SEGMENT_VALUES)[number];
export const DailySegment = enumObject(DAILY_SEGMENT_VALUES);

/** Where a full-time faculty member reports for daily attendance (§8.1, §21). */
export const WORK_ARRANGEMENT_VALUES = ['ONSITE', 'WORK_FROM_HOME', 'OFFICIAL_BUSINESS'] as const;
export type WorkArrangement = (typeof WORK_ARRANGEMENT_VALUES)[number];
export const WorkArrangement = enumObject(WORK_ARRANGEMENT_VALUES);

/** Attendance session lifecycle (§27, §24, §26). */
export const ATTENDANCE_STATUS_VALUES = [
  'SCHEDULED',
  'CLOCKED_IN',
  'CLOCKED_OUT',
  'MISSING_CLOCK_OUT',
  'PENDING_VERIFICATION',
  'FLAGGED',
  'UNDER_REVIEW',
  'VERIFIED',
  'REJECTED',
  'APPROVED',
  'PAYROLL_READY',
  'PROCESSED',
  'LOCKED',
  'CORRECTION_REQUESTED',
  'CANCELLED',
  'CLASS_SUSPENDED',
  'ABSENT',
  'NO_SHOW',
] as const;
export type AttendanceStatus = (typeof ATTENDANCE_STATUS_VALUES)[number];
export const AttendanceStatus = enumObject(ATTENDANCE_STATUS_VALUES);

export const VERIFICATION_STATUS_VALUES = [
  'NOT_STARTED',
  'PENDING',
  'AUTO_VERIFIED',
  'FLAGGED',
  'UNDER_REVIEW',
  'VERIFIED',
  'REJECTED',
] as const;
export type VerificationStatus = (typeof VERIFICATION_STATUS_VALUES)[number];
export const VerificationStatus = enumObject(VERIFICATION_STATUS_VALUES);

export const APPROVAL_STATUS_VALUES = ['NOT_STARTED', 'PENDING', 'APPROVED', 'REJECTED'] as const;
export type ApprovalStatus = (typeof APPROVAL_STATUS_VALUES)[number];
export const ApprovalStatus = enumObject(APPROVAL_STATUS_VALUES);

export const PAYROLL_STATUS_VALUES = ['NOT_READY', 'PAYROLL_READY', 'PROCESSED', 'LOCKED'] as const;
export type PayrollStatus = (typeof PAYROLL_STATUS_VALUES)[number];
export const PayrollStatus = enumObject(PAYROLL_STATUS_VALUES);

export const ATTENDANCE_FLAG_TYPE_VALUES = [
  'LATE',
  'VERY_LATE',
  'EARLY_CLOCK_OUT',
  'UNDERTIME',
  'LATE_CLOCK_OUT',
  'MISSING_CLOCK_IN',
  'MISSING_CLOCK_OUT',
  'OUTSIDE_ALLOWED_WINDOW',
  'OUTSIDE_GEOFENCE',
  'LOW_GPS_ACCURACY',
  'GPS_UNAVAILABLE',
  'GPS_PERMISSION_DENIED',
  'SUSPECTED_GPS_SPOOFING',
  'IMPOSSIBLE_TRAVEL',
  'BELOW_MINIMUM_DURATION',
  'EXCEEDS_MAXIMUM_DURATION',
  'EXCESS_MINUTES',
  'CONCURRENT_SESSION',
  'PROOF_REQUIRED',
  'MODALITY_MISMATCH',
  'OFFLINE_CAPTURE',
  'EXCESSIVE_CORRECTIONS',
  'UNAPPROVED_REMOTE_WORK',
  'LOCATION_NOT_CONFIGURED',
  'OTHER',
  /** A makeup session whose original meeting was already credited (§18.3). */
  'DUPLICATE_CREDIT',
  /** Clocked from a device not registered to the faculty member (§37, Phase 2B). */
  'UNREGISTERED_DEVICE',
  /** The device also recorded attendance for another faculty member (§37 buddy punching). */
  'SHARED_DEVICE',
  /**
   * A room code that does not fit the event: a display at another location, or an accurate GPS
   * position far from the display that was scanned — a forwarded code (§37 QR reuse, Phase 2E).
   */
  'QR_LOCATION_MISMATCH',
  /** No room code was scanned where the policy requires one (clock-outs are flagged, not refused). */
  'QR_SCAN_MISSING',
  /**
   * The device did not verify its user (fingerprint, face or device PIN) where the policy asks for
   * it (Phase 3H); clock-outs are flagged, not refused.
   */
  'USER_NOT_VERIFIED',
  /** Recorded by a biometric time clock and sent by its integration: always verified by a person (Phase 3H). */
  'TIME_CLOCK_PUNCH',
  /**
   * The meeting platform's log shows the faculty member in the online class's meeting for less
   * than the policy's share of the scheduled time (Phase 3I).
   */
  'MEETING_ATTENDANCE_SHORT',
] as const;
export type AttendanceFlagType = (typeof ATTENDANCE_FLAG_TYPE_VALUES)[number];
export const AttendanceFlagType = enumObject(ATTENDANCE_FLAG_TYPE_VALUES);

export const FLAG_SEVERITY_VALUES = ['INFO', 'WARNING', 'CRITICAL'] as const;
export type FlagSeverity = (typeof FLAG_SEVERITY_VALUES)[number];
export const FlagSeverity = enumObject(FLAG_SEVERITY_VALUES);

export const FLAG_STATUS_VALUES = ['OPEN', 'RESOLVED', 'DISMISSED'] as const;
export type FlagStatus = (typeof FLAG_STATUS_VALUES)[number];
export const FlagStatus = enumObject(FLAG_STATUS_VALUES);

/** Full-time daily status (§21). */
export const DAILY_STATUS_VALUES = [
  'PRESENT',
  'ABSENT',
  'LATE',
  'UNDERTIME',
  'ON_LEAVE',
  'OFFICIAL_BUSINESS',
  'WORK_FROM_HOME',
  'HOLIDAY',
  'WORK_SUSPENDED',
  'REST_DAY',
  'PARTIAL',
] as const;
export type DailyStatus = (typeof DAILY_STATUS_VALUES)[number];
export const DailyStatus = enumObject(DAILY_STATUS_VALUES);

// ── Rules engine (§13, §28) ────────────────────────────────────────────────────
export const ROUNDING_POLICY_VALUES = [
  'NONE',
  'NEAREST_5',
  'NEAREST_10',
  'NEAREST_15',
  'FLOOR_15',
  'CEIL_15',
] as const;
export type RoundingPolicy = (typeof ROUNDING_POLICY_VALUES)[number];
export const RoundingPolicy = enumObject(ROUNDING_POLICY_VALUES);

export const PAYABLE_HOUR_POLICY_VALUES = [
  'ACTUAL_RENDERED',
  'SCHEDULED_IF_COMPLETED',
  'MIN_OF_RENDERED_AND_SCHEDULED',
  'VERIFIED_MINUTES',
  'MANUAL_APPROVAL',
] as const;
export type PayableHourPolicy = (typeof PAYABLE_HOUR_POLICY_VALUES)[number];
export const PayableHourPolicy = enumObject(PAYABLE_HOUR_POLICY_VALUES);

/**
 * How the grace period applies to lateness (§13 example: "7 or 2 after grace, depending on policy").
 * - DEDUCT_GRACE: late = max(0, raw_late - grace)            → 10:07 with 5 min grace = 2
 * - GRACE_THRESHOLD: late = raw_late when raw_late > grace    → 10:07 with 5 min grace = 7
 */
export const LATE_POLICY_VALUES = ['DEDUCT_GRACE', 'GRACE_THRESHOLD'] as const;
export type LatePolicy = (typeof LATE_POLICY_VALUES)[number];
export const LatePolicy = enumObject(LATE_POLICY_VALUES);

// ── Academic exceptions (§16–§25) ──────────────────────────────────────────────
export const PROOF_TYPE_VALUES = [
  'ONLINE_CLASS_SCREENSHOT',
  'MEETING_ATTENDANCE_LOG',
  'LMS_ACTIVITY',
  'SESSION_LOG',
  'SYLLABUS_PROGRESS',
  'LESSON_PLAN',
  'CLASSROOM_PHOTO',
  'STUDENT_ATTENDANCE',
  'OTHER',
] as const;
export type ProofType = (typeof PROOF_TYPE_VALUES)[number];
export const ProofType = enumObject(PROOF_TYPE_VALUES);

export const PROOF_STATUS_VALUES = ['PENDING_REVIEW', 'ACCEPTED', 'REJECTED'] as const;
export type ProofStatus = (typeof PROOF_STATUS_VALUES)[number];
export const ProofStatus = enumObject(PROOF_STATUS_VALUES);

export const MAKEUP_STATUS_VALUES = [
  'DRAFT',
  'SUBMITTED',
  'CHAIR_REVIEW',
  'DEAN_REVIEW_OPTIONAL',
  'APPROVED',
  'REJECTED',
  'CANCELLED',
  'COMPLETED',
] as const;
export type MakeupStatus = (typeof MAKEUP_STATUS_VALUES)[number];
export const MakeupStatus = enumObject(MAKEUP_STATUS_VALUES);

/** Generic request/approval status used by substitutions, overloads, duties, leave and corrections. */
export const REQUEST_STATUS_VALUES = [
  'DRAFT',
  'SUBMITTED',
  'UNDER_REVIEW',
  'APPROVED',
  'REJECTED',
  'CANCELLED',
  'COMPLETED',
] as const;
export type RequestStatus = (typeof REQUEST_STATUS_VALUES)[number];
export const RequestStatus = enumObject(REQUEST_STATUS_VALUES);

export const SUBSTITUTE_COMPENSATION_POLICY_VALUES = [
  'SUBSTITUTE_PAYABLE',
  'ORIGINAL_PAYABLE',
  'NO_ADDITIONAL_PAY',
  'EXTRA_LOAD',
  'MANUAL_REVIEW',
] as const;
export type SubstituteCompensationPolicy = (typeof SUBSTITUTE_COMPENSATION_POLICY_VALUES)[number];
export const SubstituteCompensationPolicy = enumObject(SUBSTITUTE_COMPENSATION_POLICY_VALUES);

export const OVERLOAD_ASSIGNMENT_TYPE_VALUES = [
  'OVERLOAD_TEACHING',
  'ADDITIONAL_LECTURE',
  'LABORATORY_SUPERVISION',
  'CLINICAL_SUPERVISION',
  'SEMINAR',
  'SPECIAL_CONSULTATION',
  'INSTITUTIONAL_DUTY',
  'OTHER',
] as const;
export type OverloadAssignmentType = (typeof OVERLOAD_ASSIGNMENT_TYPE_VALUES)[number];
export const OverloadAssignmentType = enumObject(OVERLOAD_ASSIGNMENT_TYPE_VALUES);

/** Seeded duty categories (§22). Duty categories are configurable rows. */
export const DUTY_CATEGORY_CODES = [
  'DEPARTMENT_ADMINISTRATION',
  'DEANS_OFFICE_WORK',
  'COMMITTEE_MEETING',
  'FACULTY_MEETING',
  'CURRICULUM_WORK',
  'ACCREDITATION',
  'RESEARCH',
  'ADVISING',
  'STUDENT_CONSULTATION',
  'FACULTY_DEVELOPMENT',
  'INSTITUTIONAL_EVENT',
  'OFFICIAL_BUSINESS',
  'OTHER',
] as const;
export type DutyCategoryCode = (typeof DUTY_CATEGORY_CODES)[number];
export const DutyCategoryCode = enumObject(DUTY_CATEGORY_CODES);

export const LEAVE_TYPE_VALUES = [
  'VACATION',
  'SICK',
  'EMERGENCY',
  'MATERNITY',
  'PATERNITY',
  'BEREAVEMENT',
  'STUDY',
  'OFFICIAL_BUSINESS',
  'OTHER',
] as const;
export type LeaveType = (typeof LEAVE_TYPE_VALUES)[number];
export const LeaveType = enumObject(LEAVE_TYPE_VALUES);

// ── Leave policies and balances (§23 "advanced leave functions", §67, Phase 2F) ─
/** What happens when a request exceeds the balance: not checked, a warning for approvers, or refused. */
export const LEAVE_BALANCE_CHECK_VALUES = ['NONE', 'WARN', 'BLOCK'] as const;
export type LeaveBalanceCheck = (typeof LEAVE_BALANCE_CHECK_VALUES)[number];
export const LeaveBalanceCheck = enumObject(LEAVE_BALANCE_CHECK_VALUES);

/** The whole year's entitlement on January 1, or 1/12 at the start of each month. */
export const LEAVE_ACCRUAL_VALUES = ['ANNUAL', 'MONTHLY'] as const;
export type LeaveAccrual = (typeof LEAVE_ACCRUAL_VALUES)[number];
export const LeaveAccrual = enumObject(LEAVE_ACCRUAL_VALUES);

/** When a request needs an attachment (e.g. a medical certificate) before it can be approved. */
export const LEAVE_ATTACHMENT_RULE_VALUES = ['NEVER', 'ALWAYS', 'OVER_DAYS'] as const;
export type LeaveAttachmentRule = (typeof LEAVE_ATTACHMENT_RULE_VALUES)[number];
export const LeaveAttachmentRule = enumObject(LEAVE_ATTACHMENT_RULE_VALUES);

/** Ledger entries: HR adjustments with a reason, and the carry-over written at the start of a year. */
export const LEAVE_LEDGER_KIND_VALUES = ['ADJUSTMENT', 'CARRY_OVER'] as const;
export type LeaveLedgerKind = (typeof LEAVE_LEDGER_KIND_VALUES)[number];
export const LeaveLedgerKind = enumObject(LEAVE_LEDGER_KIND_VALUES);

export const CALENDAR_EVENT_TYPE_VALUES = [
  'NATIONAL_HOLIDAY',
  'LOCAL_HOLIDAY',
  'SCHOOL_HOLIDAY',
  'CLASS_SUSPENSION',
  'WORK_SUSPENSION',
  'FACULTY_DEVELOPMENT',
  'INSTITUTIONAL_ACTIVITY',
  'OTHER',
] as const;
export type CalendarEventType = (typeof CALENDAR_EVENT_TYPE_VALUES)[number];
export const CalendarEventType = enumObject(CALENDAR_EVENT_TYPE_VALUES);

export const CORRECTION_REASON_VALUES = [
  'FORGOT_CLOCK_IN',
  'FORGOT_CLOCK_OUT',
  'GPS_FAILURE',
  'INTERNET_FAILURE',
  'SYSTEM_OUTAGE',
  'INCORRECT_SCHEDULE',
  'OFFICIAL_ASSIGNMENT',
  'EMERGENCY',
  'WRONG_MODALITY',
  'OTHER',
] as const;
export type CorrectionReason = (typeof CORRECTION_REASON_VALUES)[number];
export const CorrectionReason = enumObject(CORRECTION_REASON_VALUES);

/** Session values a correction may change (§25.3 "requested field"). */
export const CORRECTION_FIELD_VALUES = ['actualClockIn', 'actualClockOut', 'actualModality', 'workArrangement'] as const;
export type CorrectionField = (typeof CORRECTION_FIELD_VALUES)[number];
export const CorrectionField = enumObject(CORRECTION_FIELD_VALUES);

/** How an overload or extra-load assignment is compensated (§20 "compensation category"). */
export const COMPENSATION_CATEGORY_VALUES = ['OVERLOAD_PAY', 'EXTRA_LOAD_PAY', 'HONORARIUM', 'SERVICE_CREDIT', 'NO_COMPENSATION'] as const;
export type CompensationCategory = (typeof COMPENSATION_CATEGORY_VALUES)[number];
export const CompensationCategory = enumObject(COMPENSATION_CATEGORY_VALUES);

/**
 * Whether a session may earn payable credit (§18.3, §19):
 * - STANDARD: credited under the payable-hour policy once approved.
 * - NON_PAYABLE: recorded for attendance only (e.g. duplicate makeup, substitution without pay).
 * - MANUAL_REVIEW: an approver decides the credit.
 */
export const SESSION_CREDIT_POLICY_VALUES = ['STANDARD', 'NON_PAYABLE', 'MANUAL_REVIEW'] as const;
export type SessionCreditPolicy = (typeof SESSION_CREDIT_POLICY_VALUES)[number];
export const SessionCreditPolicy = enumObject(SESSION_CREDIT_POLICY_VALUES);

// ── Approval workflows (§64) ───────────────────────────────────────────────────
export const WORKFLOW_CODE_VALUES = ['ATTENDANCE_CORRECTION', 'MAKEUP', 'SUBSTITUTION', 'OVERLOAD', 'DUTY', 'LEAVE'] as const;
export type WorkflowCode = (typeof WORKFLOW_CODE_VALUES)[number];
export const WorkflowCode = enumObject(WORKFLOW_CODE_VALUES);

/** When a workflow step applies. PAYROLL_RELEVANT: only for requests that change credited minutes. */
export const WORKFLOW_STEP_CONDITION_VALUES = ['ALWAYS', 'PAYROLL_RELEVANT'] as const;
export type WorkflowStepCondition = (typeof WORKFLOW_STEP_CONDITION_VALUES)[number];
export const WorkflowStepCondition = enumObject(WORKFLOW_STEP_CONDITION_VALUES);

export const APPROVAL_REQUEST_STATUS_VALUES = ['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'] as const;
export type ApprovalRequestStatus = (typeof APPROVAL_REQUEST_STATUS_VALUES)[number];
export const ApprovalRequestStatus = enumObject(APPROVAL_REQUEST_STATUS_VALUES);

/** ESCALATED: the pending step waited past its escalation time and widened to the next scope level (Phase 2G). */
export const APPROVAL_ACTION_VALUES = ['SUBMITTED', 'APPROVED', 'REJECTED', 'CANCELLED', 'ESCALATED'] as const;
export type ApprovalAction = (typeof APPROVAL_ACTION_VALUES)[number];
export const ApprovalAction = enumObject(APPROVAL_ACTION_VALUES);

// ── Payroll support (§32) ──────────────────────────────────────────────────────
export const PAYROLL_PERIOD_STATUS_VALUES = [
  'OPEN',
  'UNDER_REVIEW',
  'SUBMITTED',
  'LOCKED',
  'PROCESSED',
  'REOPENED_WITH_AUTHORIZATION',
] as const;
export type PayrollPeriodStatus = (typeof PAYROLL_PERIOD_STATUS_VALUES)[number];
export const PayrollPeriodStatus = enumObject(PAYROLL_PERIOD_STATUS_VALUES);

/**
 * Cut-off states in which attendance of the period is frozen for ordinary changes (§32.3):
 * finalized (payroll-ready), locked and processed. A reopened period accepts changes only
 * from holders of `attendance.post_lock_adjust`.
 */
export const FROZEN_PAYROLL_PERIOD_STATUSES: readonly PayrollPeriodStatus[] = ['SUBMITTED', 'LOCKED', 'PROCESSED'];

/** Recorded transitions of a payroll cut-off (append-only history, §32.2). */
export const PAYROLL_PERIOD_ACTION_VALUES = [
  'CREATE',
  'UPDATE',
  'START_REVIEW',
  'APPROVE_DEPARTMENT',
  'FINALIZE',
  'RETURN_TO_REVIEW',
  'LOCK',
  'MARK_PROCESSED',
  'REOPEN',
] as const;
export type PayrollPeriodAction = (typeof PAYROLL_PERIOD_ACTION_VALUES)[number];
export const PayrollPeriodAction = enumObject(PAYROLL_PERIOD_ACTION_VALUES);

/** Reviewer actions on one attendance session (append-only trail, §27, §76 rule 16). */
export const SESSION_REVIEW_ACTION_VALUES = ['START_REVIEW', 'VERIFY', 'REJECT', 'APPROVE', 'RETURN', 'ADJUST'] as const;
export type SessionReviewAction = (typeof SESSION_REVIEW_ACTION_VALUES)[number];
export const SessionReviewAction = enumObject(SESSION_REVIEW_ACTION_VALUES);

/** Hour categories on payroll-ready reports (§33.1): regular load, makeup, substitute, extra load. */
export const PAYROLL_LOAD_CATEGORY_VALUES = ['REGULAR', 'MAKEUP', 'SUBSTITUTE', 'EXTRA_LOAD'] as const;
export type PayrollLoadCategory = (typeof PAYROLL_LOAD_CATEGORY_VALUES)[number];
export const PayrollLoadCategory = enumObject(PAYROLL_LOAD_CATEGORY_VALUES);

// ── Reports and exports (§33.3, §35, §46.9) ───────────────────────────────────
/** XLSX is the primary payroll handoff; HTML is the printable page (§33.3). */
export const EXPORT_FORMAT_VALUES = ['xlsx', 'csv', 'pdf', 'html'] as const;
export type ExportFormat = (typeof EXPORT_FORMAT_VALUES)[number];
export const ExportFormat = enumObject(EXPORT_FORMAT_VALUES);

/** Grouping of the attendance summary report: daily/weekly/monthly, per faculty, department or college. */
export const REPORT_GROUP_BY_VALUES = ['date', 'week', 'month', 'faculty', 'department', 'college'] as const;
export type ReportGroupBy = (typeof REPORT_GROUP_BY_VALUES)[number];
export const ReportGroupBy = enumObject(REPORT_GROUP_BY_VALUES);

/** Periods of the trends report (Phase 2H): ISO weeks, calendar months or semesters. */
export const TREND_INTERVAL_VALUES = ['week', 'month', 'term'] as const;
export type TrendInterval = (typeof TREND_INTERVAL_VALUES)[number];
export const TrendInterval = enumObject(TREND_INTERVAL_VALUES);

/** Session-level exception reports (§35 "Exceptions", §46.9); each is served at `/reports/<kind>`. */
export const SESSION_REPORT_KIND_VALUES = ['tardiness', 'absences', 'missing-clock-outs', 'undertime', 'outside-geofence', 'rejected-attendance'] as const;
export type SessionReportKind = (typeof SESSION_REPORT_KIND_VALUES)[number];

// ── Devices (§37 "optional device registration", Phase 2B) ────────────────────
/** Attendance-policy rule: whether clock events must come from a registered device. */
export const DEVICE_REGISTRATION_MODE_VALUES = ['OFF', 'FLAG', 'REQUIRE'] as const;
export type DeviceRegistrationMode = (typeof DEVICE_REGISTRATION_MODE_VALUES)[number];
export const DeviceRegistrationMode = enumObject(DEVICE_REGISTRATION_MODE_VALUES);

/**
 * Policy rule `userVerification` (Phase 3H): whether clock events need the device to verify its
 * user (WebAuthn user verification — fingerprint, face or device PIN). Same levels as devices.
 */
export const USER_VERIFICATION_MODE_VALUES = ['OFF', 'FLAG', 'REQUIRE'] as const;
export type UserVerificationMode = (typeof USER_VERIFICATION_MODE_VALUES)[number];
export const UserVerificationMode = enumObject(USER_VERIFICATION_MODE_VALUES);

/** Lifecycle of a WebAuthn credential (Phase 3H). */
export const WEBAUTHN_CREDENTIAL_STATUS_VALUES = ['ACTIVE', 'REVOKED'] as const;
export type WebauthnCredentialStatus = (typeof WEBAUTHN_CREDENTIAL_STATUS_VALUES)[number];
export const WebauthnCredentialStatus = enumObject(WEBAUTHN_CREDENTIAL_STATUS_VALUES);

/** A biometric time clock registered for punches through the integration API (Phase 3H). */
export const TIME_CLOCK_STATUS_VALUES = ['ACTIVE', 'REVOKED'] as const;
export type TimeClockStatus = (typeof TIME_CLOCK_STATUS_VALUES)[number];
export const TimeClockStatus = enumObject(TIME_CLOCK_STATUS_VALUES);

/**
 * What a time-clock punch became (Phase 3H): recorded as a clock-in or clock-out, replayed (sent
 * before), ignored (a repeat of the person's previous punch within minutes), or rejected.
 */
export const TIME_CLOCK_PUNCH_OUTCOME_VALUES = ['RECORDED', 'REPLAYED', 'IGNORED', 'REJECTED'] as const;
export type TimeClockPunchOutcome = (typeof TIME_CLOCK_PUNCH_OUTCOME_VALUES)[number];

// ── Native app (Phase 3J) ──────────────────────────────────────────────────────
/** How a device receives pushes: Web Push in browsers, Firebase Cloud Messaging in the Android app. */
export const PUSH_CHANNEL_VALUES = ['WEB_PUSH', 'FCM'] as const;
export type PushChannel = (typeof PUSH_CHANNEL_VALUES)[number];
export const PushChannel = enumObject(PUSH_CHANNEL_VALUES);

// ── Meeting platforms (Phase 3I) ───────────────────────────────────────────────
/** Where a meeting event came from: Zoom's signed webhook, or an integration client. */
export const MEETING_EVENT_SOURCE_VALUES = ['ZOOM_WEBHOOK', 'INTEGRATION_CLIENT'] as const;
export type MeetingEventSource = (typeof MEETING_EVENT_SOURCE_VALUES)[number];
export const MeetingEventSource = enumObject(MEETING_EVENT_SOURCE_VALUES);

/** A faculty member joined or left an online meeting. */
export const MEETING_EVENT_TYPE_VALUES = ['JOINED', 'LEFT'] as const;
export type MeetingEventType = (typeof MEETING_EVENT_TYPE_VALUES)[number];
export const MeetingEventType = enumObject(MEETING_EVENT_TYPE_VALUES);

/**
 * What a received meeting event became: recorded as evidence for an online class, replayed (sent
 * before), ignored (not a faculty member, or no online class of theirs at that time — not
 * stored), or rejected (invalid).
 */
export const MEETING_EVENT_OUTCOME_VALUES = ['RECORDED', 'REPLAYED', 'IGNORED', 'REJECTED'] as const;
export type MeetingEventOutcome = (typeof MEETING_EVENT_OUTCOME_VALUES)[number];

/** Lifecycle of a registered device. */
export const USER_DEVICE_STATUS_VALUES = ['PENDING_APPROVAL', 'ACTIVE', 'REVOKED'] as const;
export type UserDeviceStatus = (typeof USER_DEVICE_STATUS_VALUES)[number];
export const UserDeviceStatus = enumObject(USER_DEVICE_STATUS_VALUES);

/**
 * What the server established about the device behind one clock event: a proven device key
 * registered to the faculty member (or awaiting approval, or revoked), a proven key that is
 * not registered to them, no proof at all, or a proof that failed verification.
 */
export const DEVICE_CHECK_STATUS_VALUES = ['REGISTERED', 'PENDING_APPROVAL', 'REVOKED', 'UNREGISTERED', 'NO_PROOF', 'INVALID_PROOF'] as const;
export type DeviceCheckStatus = (typeof DEVICE_CHECK_STATUS_VALUES)[number];
export const DeviceCheckStatus = enumObject(DEVICE_CHECK_STATUS_VALUES);

/** Setting `devices.approval`: which registrations wait for approval. */
export const DEVICE_APPROVAL_POLICY_VALUES = ['NONE', 'ADDITIONAL', 'ALWAYS'] as const;
export type DeviceApprovalPolicy = (typeof DEVICE_APPROVAL_POLICY_VALUES)[number];
export const DeviceApprovalPolicy = enumObject(DEVICE_APPROVAL_POLICY_VALUES);

/**
 * What a device proof is for; part of the signed message. Offline clock events (Phase 2D) are
 * signed on the device without a server challenge.
 */
export const DEVICE_PROOF_PURPOSE_VALUES = ['REGISTER', 'CLOCK_IN', 'CLOCK_OUT', 'OFFLINE_CLOCK_IN', 'OFFLINE_CLOCK_OUT', 'EVENT_CHECK_IN'] as const;
export type DeviceProofPurpose = (typeof DEVICE_PROOF_PURPOSE_VALUES)[number];

/** Outcome of one clock event recorded offline and sent later (§57, Phase 2D). */
export const OFFLINE_SYNC_OUTCOME_VALUES = ['ACCEPTED', 'REPLAYED', 'REJECTED'] as const;
export type OfflineSyncOutcome = (typeof OFFLINE_SYNC_OUTCOME_VALUES)[number];

// ── Attendance displays and room codes (§37 dynamic QR, Phase 2E) ──────────────
/** ROOM: shows codes for one room or laboratory. KIOSK: for the building (or campus) of its location. */
export const ATTENDANCE_DISPLAY_KIND_VALUES = ['ROOM', 'KIOSK'] as const;
export type AttendanceDisplayKind = (typeof ATTENDANCE_DISPLAY_KIND_VALUES)[number];
export const AttendanceDisplayKind = enumObject(ATTENDANCE_DISPLAY_KIND_VALUES);

export const ATTENDANCE_DISPLAY_STATUS_VALUES = ['PENDING_PAIRING', 'ACTIVE', 'REVOKED'] as const;
export type AttendanceDisplayStatus = (typeof ATTENDANCE_DISPLAY_STATUS_VALUES)[number];
export const AttendanceDisplayStatus = enumObject(ATTENDANCE_DISPLAY_STATUS_VALUES);

/** What a scan identifies: a display's code (D), an event's own check-in code (E) or an NFC room tag (T). */
export const SCAN_TARGET_KIND_VALUES = ['DISPLAY', 'EVENT', 'ROOM_TAG'] as const;
export type ScanTargetKind = (typeof SCAN_TARGET_KIND_VALUES)[number];

// ── ID cards and NFC room tags (Phase 3G, ADR-062) ─────────────────────────────
export const ATTENDANCE_CARD_STATUS_VALUES = ['ACTIVE', 'REVOKED'] as const;
export type AttendanceCardStatus = (typeof ATTENDANCE_CARD_STATUS_VALUES)[number];
export const AttendanceCardStatus = enumObject(ATTENDANCE_CARD_STATUS_VALUES);

export const ROOM_TAG_STATUS_VALUES = ['ACTIVE', 'REVOKED'] as const;
export type RoomTagStatus = (typeof ROOM_TAG_STATUS_VALUES)[number];
export const RoomTagStatus = enumObject(ROOM_TAG_STATUS_VALUES);

// ── Institutional events (§8.1, §22, §67, Phase 2E) ────────────────────────────
export const INSTITUTIONAL_EVENT_TYPE_VALUES = ['MEETING', 'CEREMONY', 'SEMINAR', 'TRAINING', 'ASSEMBLY', 'OTHER'] as const;
export type InstitutionalEventType = (typeof INSTITUTIONAL_EVENT_TYPE_VALUES)[number];
export const InstitutionalEventType = enumObject(INSTITUTIONAL_EVENT_TYPE_VALUES);

export const INSTITUTIONAL_EVENT_STATUS_VALUES = ['DRAFT', 'PUBLISHED', 'CANCELLED'] as const;
export type InstitutionalEventStatus = (typeof INSTITUTIONAL_EVENT_STATUS_VALUES)[number];
export const InstitutionalEventStatus = enumObject(INSTITUTIONAL_EVENT_STATUS_VALUES);

/** How attendees may check in: only by scanning a code, or also in the app (with GPS on site). */
export const EVENT_CHECK_IN_MODE_VALUES = ['QR_ONLY', 'QR_OR_APP'] as const;
export type EventCheckInMode = (typeof EVENT_CHECK_IN_MODE_VALUES)[number];
export const EventCheckInMode = enumObject(EVENT_CHECK_IN_MODE_VALUES);

/** Who is invited: everyone, a campus, college, department or employment type, or one faculty member. */
export const EVENT_AUDIENCE_SCOPE_VALUES = ['INSTITUTION', 'CAMPUS', 'COLLEGE', 'DEPARTMENT', 'EMPLOYMENT_TYPE', 'FACULTY'] as const;
export type EventAudienceScope = (typeof EVENT_AUDIENCE_SCOPE_VALUES)[number];
export const EventAudienceScope = enumObject(EVENT_AUDIENCE_SCOPE_VALUES);

/** One faculty member's attendance at an event. EXPECTED until they check in or the event is closed. */
export const EVENT_ATTENDANCE_STATUS_VALUES = ['EXPECTED', 'PRESENT', 'LATE', 'EXCUSED', 'ABSENT'] as const;
export type EventAttendanceStatus = (typeof EVENT_ATTENDANCE_STATUS_VALUES)[number];
export const EventAttendanceStatus = enumObject(EVENT_ATTENDANCE_STATUS_VALUES);

/** Why an attendee is on the list: the audience, a walk-in check-in, or added by the organizer. */
export const EVENT_ATTENDEE_SOURCE_VALUES = ['AUDIENCE', 'WALK_IN', 'ADDED'] as const;
export type EventAttendeeSource = (typeof EVENT_ATTENDEE_SOURCE_VALUES)[number];
export const EventAttendeeSource = enumObject(EVENT_ATTENDEE_SOURCE_VALUES);

// ── Notifications (§36) ────────────────────────────────────────────────────────
export const NOTIFICATION_CHANNEL_VALUES = ['IN_APP', 'EMAIL', 'PUSH', 'SMS'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNEL_VALUES)[number];
export const NotificationChannel = enumObject(NOTIFICATION_CHANNEL_VALUES);

export const NOTIFICATION_EVENT_TYPE_VALUES = [
  'CLASS_STARTS_SOON',
  'CLOCK_IN_REMINDER',
  'MISSING_CLOCK_OUT',
  'LATE_FLAG',
  'OUTSIDE_GEOFENCE_REVIEW',
  'MAKEUP_DECISION',
  'SUBSTITUTE_ASSIGNMENT',
  'CORRECTION_DECISION',
  'ATTENDANCE_AWAITING_APPROVAL',
  'PAYROLL_CUTOFF_APPROACHING',
  'CUTOFF_LOCKED',
  'PASSWORD_CHANGED',
  'ACCOUNT_LOCKED',
  'GENERAL',
  /** A request reached a workflow step the recipient can decide. */
  'APPROVAL_REQUESTED',
  /** Decision on an overload, duty or leave request. */
  'REQUEST_DECISION',
  /** A reviewer verified, rejected or returned one of the recipient's attendance sessions. */
  'ATTENDANCE_REVIEWED',
  /** A device was registered to the recipient's account, approved or removed (security notice). */
  'DEVICE_REGISTRATION',
  /** Invitation to, change of, reminder of or cancellation of an institutional event (Phase 2E). */
  'INSTITUTIONAL_EVENT',
] as const;
export type NotificationEventType = (typeof NOTIFICATION_EVENT_TYPE_VALUES)[number];
export const NotificationEventType = enumObject(NOTIFICATION_EVENT_TYPE_VALUES);

export interface NotificationTypeInfo {
  label: string;
  description: string;
  /** Default channels; the sender may request e-mail for important cases of a type. */
  inApp: boolean;
  email: boolean;
  /** Push to the user's subscribed devices (Phase 2C); only where push is set up. */
  push: boolean;
  /**
   * A text message to the user's confirmed mobile number (§36 SMS, ADR-069) — only where SMS is
   * set up and the institution allows the type (`notifications.sms_event_types`). Optional even
   * for security notices: texts cost money.
   */
  sms: boolean;
  /** Security-critical: cannot be turned off (§36). */
  mandatory?: boolean;
}

/** Delivery of a text message (ADR-069). */
export const SMS_MESSAGE_STATUS_VALUES = ['PENDING', 'SENT', 'FAILED', 'EXPIRED'] as const;
export type SmsMessageStatus = (typeof SMS_MESSAGE_STATUS_VALUES)[number];
export const SmsMessageStatus = enumObject(SMS_MESSAGE_STATUS_VALUES);

/** Labels and default channels of every notification type, for preferences (§36). */
export const NOTIFICATION_TYPE_INFO: Record<NotificationEventType, NotificationTypeInfo> = {
  CLASS_STARTS_SOON: { label: 'Class reminder', description: 'Shortly before each class or duty that needs clocking', inApp: true, email: false, push: true, sms: false },
  CLOCK_IN_REMINDER: { label: 'Clock-in reminder', description: 'When a clock-in is due and has not been recorded', inApp: true, email: false, push: true, sms: false },
  MISSING_CLOCK_OUT: { label: 'Missing clock-out', description: 'When no clock-out was recorded and a correction is needed', inApp: true, email: true, push: true, sms: true },
  LATE_FLAG: { label: 'Late arrival', description: 'When a clock-in is recorded as late', inApp: true, email: false, push: false, sms: false },
  OUTSIDE_GEOFENCE_REVIEW: { label: 'Location review', description: 'When a clock event is flagged for its location', inApp: true, email: false, push: false, sms: false },
  MAKEUP_DECISION: { label: 'Makeup class result', description: 'Approval or rejection of your makeup requests', inApp: true, email: true, push: true, sms: false },
  SUBSTITUTE_ASSIGNMENT: { label: 'Substitute assignment', description: 'When you are assigned to substitute a class', inApp: true, email: true, push: true, sms: false },
  CORRECTION_DECISION: { label: 'Correction result', description: 'Approval or rejection of your correction requests', inApp: true, email: true, push: true, sms: false },
  ATTENDANCE_AWAITING_APPROVAL: { label: 'Attendance awaiting your review', description: 'Sessions in your scope that need verification or approval for a cut-off', inApp: true, email: false, push: false, sms: false },
  PAYROLL_CUTOFF_APPROACHING: { label: 'Cut-off reminder', description: 'Before a payroll cut-off deadline when items still need action', inApp: true, email: true, push: true, sms: false },
  CUTOFF_LOCKED: { label: 'Cut-off locked', description: 'When a payroll cut-off containing your attendance is locked or reopened', inApp: true, email: false, push: false, sms: false },
  PASSWORD_CHANGED: { label: 'Password changed', description: 'Security notice when your password changes', inApp: true, email: true, push: true, sms: true, mandatory: true },
  ACCOUNT_LOCKED: { label: 'Account locked', description: 'Security notice after repeated failed sign-ins', inApp: true, email: true, push: true, sms: true, mandatory: true },
  GENERAL: { label: 'General announcements', description: 'Other messages from the system', inApp: true, email: false, push: false, sms: false },
  APPROVAL_REQUESTED: { label: 'Approval requests', description: 'A request reached a step you can decide', inApp: true, email: false, push: true, sms: false },
  REQUEST_DECISION: { label: 'Request results', description: 'Decisions on your substitution, overload, duty and leave requests', inApp: true, email: true, push: true, sms: false },
  ATTENDANCE_REVIEWED: { label: 'Attendance review results', description: 'When a reviewer rejects or returns one of your sessions', inApp: true, email: true, push: true, sms: false },
  DEVICE_REGISTRATION: {
    label: 'Device registration',
    description: 'Security notice when a device is registered to your account, approved or removed',
    inApp: true,
    email: true,
    push: true,
    sms: true,
    mandatory: true,
  },
  INSTITUTIONAL_EVENT: {
    label: 'Institutional events',
    description: 'Invitations to meetings, ceremonies and seminars, their changes, reminders and cancellations',
    inApp: true,
    email: true,
    push: true,
    sms: false,
  },
};

/** Types the institution allows to be texted unless it changes `notifications.sms_event_types` (ADR-069). */
export const DEFAULT_SMS_EVENT_TYPES: readonly NotificationEventType[] = [
  'CLASS_STARTS_SOON',
  'CLOCK_IN_REMINDER',
  'MISSING_CLOCK_OUT',
  'SUBSTITUTE_ASSIGNMENT',
  'PAYROLL_CUTOFF_APPROACHING',
  'PASSWORD_CHANGED',
  'ACCOUNT_LOCKED',
  'DEVICE_REGISTRATION',
];

// ── Audit (§38) ────────────────────────────────────────────────────────────────
export const AUDIT_CATEGORY_VALUES = [
  'AUTHENTICATION',
  'SECURITY',
  'USER_MANAGEMENT',
  'PERMISSIONS',
  'ORGANIZATION',
  'ACADEMIC',
  'FACULTY_PROFILE',
  'SCHEDULE',
  'ATTENDANCE',
  'APPROVAL',
  'CORRECTION',
  'MAKEUP',
  'SUBSTITUTE',
  'OVERLOAD',
  'LEAVE',
  'CALENDAR',
  'PAYROLL_CUTOFF',
  'REPORT_EXPORT',
  'SYSTEM_CONFIGURATION',
  'FILE_UPLOAD',
  'NOTIFICATION',
  'DUTY',
  /** Institutional events: set-up, audience, check-ins and attendance marks (Phase 2E). */
  'EVENT',
  /** Integration clients, their secrets and roles, webhooks and deliveries (Phase 3A). */
  'INTEGRATION',
] as const;
export type AuditCategory = (typeof AUDIT_CATEGORY_VALUES)[number];
export const AuditCategory = enumObject(AUDIT_CATEGORY_VALUES);

// ── Integrations (SPEC §68, Phase 3A) ───────────────────────────────────────────
/** A webhook delivery waits (and is retried), was accepted with a 2xx answer, or gave up. */
export const WEBHOOK_DELIVERY_STATUS_VALUES = ['PENDING', 'SUCCEEDED', 'FAILED'] as const;
export type WebhookDeliveryStatus = (typeof WEBHOOK_DELIVERY_STATUS_VALUES)[number];
export const WebhookDeliveryStatus = enumObject(WEBHOOK_DELIVERY_STATUS_VALUES);

/** An integration client's secret: usable, past its expiry, or revoked. */
export const CLIENT_SECRET_STATUS_VALUES = ['ACTIVE', 'EXPIRED', 'REVOKED'] as const;
export type ClientSecretStatus = (typeof CLIENT_SECRET_STATUS_VALUES)[number];

/**
 * A data sync submitted by an integration client (Phase 3C): waiting, being processed, a dry
 * run done (nothing kept), committed, rejected (an item failed: nothing kept) or failed unexpectedly.
 */
export const INTEGRATION_SYNC_STATUS_VALUES = ['QUEUED', 'RUNNING', 'DRY_RUN', 'COMPLETED', 'REJECTED', 'FAILED'] as const;
export type IntegrationSyncStatus = (typeof INTEGRATION_SYNC_STATUS_VALUES)[number];
export const IntegrationSyncStatus = enumObject(INTEGRATION_SYNC_STATUS_VALUES);

/** What happened to one item of a sync. */
export const SYNC_ITEM_OUTCOME_VALUES = ['CREATED', 'UPDATED', 'ADOPTED', 'UNCHANGED', 'DEACTIVATED', 'ERROR'] as const;
export type SyncItemOutcome = (typeof SYNC_ITEM_OUTCOME_VALUES)[number];

/** Which system a sync comes from: the SIS (Phase 3C) or the HRIS (Phase 3D). */
export const INTEGRATION_SYNC_KIND_VALUES = ['SIS', 'HRIS'] as const;
export type IntegrationSyncKind = (typeof INTEGRATION_SYNC_KIND_VALUES)[number];

/** Kinds of records an SIS sync maintains, in processing order. */
export const SIS_SYNC_KIND_VALUES = ['academicYear', 'term', 'course', 'section', 'load', 'schedule'] as const;
/** Kinds of records an HRIS sync maintains, in processing order. */
export const HRIS_SYNC_KIND_VALUES = ['faculty', 'calendarEvent', 'leave'] as const;
export type SyncItemKind = (typeof SIS_SYNC_KIND_VALUES)[number] | (typeof HRIS_SYNC_KIND_VALUES)[number];

/** Why an account is inactive when the system set it (Phase 3D): the faculty member was separated or made inactive. */
export const USER_STATUS_REASON_VALUES = ['EMPLOYMENT_ENDED'] as const;
export type UserStatusReason = (typeof USER_STATUS_REASON_VALUES)[number];

// ── Anomaly detection (Phase 3F, ADR-061) ─────────────────────────────────────
/**
 * Patterns across clock events that a single clock event cannot show: the same exact position
 * on several days, GPS readings no real receiver produces, several people clocking in on one
 * device within minutes, and a position far from where the person usually is at that location.
 */
export const ANOMALY_TYPE_VALUES = ['REPEATED_POSITION', 'IMPLAUSIBLE_GPS', 'CLUSTERED_CLOCK_EVENTS', 'UNUSUAL_POSITION'] as const;
export type AnomalyType = (typeof ANOMALY_TYPE_VALUES)[number];
export const AnomalyType = enumObject(ANOMALY_TYPE_VALUES);

/** A finding waits for review, was confirmed (the reviewer acted on it) or dismissed (no issue). */
export const ANOMALY_STATUS_VALUES = ['OPEN', 'CONFIRMED', 'DISMISSED'] as const;
export type AnomalyStatus = (typeof ANOMALY_STATUS_VALUES)[number];

// ── Enterprise single sign-on (Phase 3E, ADR-060) ────────────────────────────
/** How a session was opened: with the password here, or through the identity provider. */
export const AUTH_METHOD_VALUES = ['PASSWORD', 'SSO'] as const;
export type AuthMethod = (typeof AUTH_METHOD_VALUES)[number];

/**
 * Why a single sign-on attempt came back to the sign-in page (`/login?sso_error=`): cancelled
 * at the identity provider, expired (the attempt took too long or was replayed), no account
 * for the person, the account is inactive, the provider did not confirm the e-mail address,
 * or anything else (failed).
 */
export const SSO_ERROR_VALUES = ['cancelled', 'expired', 'no_account', 'inactive', 'unverified_email', 'failed'] as const;
export type SsoError = (typeof SSO_ERROR_VALUES)[number];

// ── Files (§16, §52) ───────────────────────────────────────────────────────────
export const FILE_CLASSIFICATION_VALUES = ['CONFIDENTIAL', 'INTERNAL', 'PUBLIC'] as const;
export type FileClassification = (typeof FILE_CLASSIFICATION_VALUES)[number];
export const FileClassification = enumObject(FILE_CLASSIFICATION_VALUES);

/** Upload formats (§16). The allowed subset is configurable (§70 "Uploads"). */
export const UPLOAD_FILE_TYPE_VALUES = ['JPG', 'PNG', 'PDF', 'DOCX', 'XLSX', 'CSV'] as const;
export type UploadFileType = (typeof UPLOAD_FILE_TYPE_VALUES)[number];
export const UploadFileType = enumObject(UPLOAD_FILE_TYPE_VALUES);

/** Canonical MIME type and accepted extensions per upload format. */
export const UPLOAD_FILE_TYPES: Record<UploadFileType, { mime: string; extensions: readonly string[]; label: string }> = {
  JPG: { mime: 'image/jpeg', extensions: ['jpg', 'jpeg'], label: 'JPEG image' },
  PNG: { mime: 'image/png', extensions: ['png'], label: 'PNG image' },
  PDF: { mime: 'application/pdf', extensions: ['pdf'], label: 'PDF document' },
  DOCX: { mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', extensions: ['docx'], label: 'Word document' },
  XLSX: { mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', extensions: ['xlsx'], label: 'Excel workbook' },
  CSV: { mime: 'text/csv', extensions: ['csv'], label: 'CSV file' },
};

export const DAYS_OF_WEEK = [1, 2, 3, 4, 5, 6, 7] as const;
/** ISO-8601 day of week: 1 = Monday … 7 = Sunday. */
export type IsoDayOfWeek = (typeof DAYS_OF_WEEK)[number];
export const DAY_OF_WEEK_LABELS: Record<IsoDayOfWeek, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};
